/*
 *  (c) 2015 - 2021 ENISCO by FORCAM GmbH
 */

import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {translateText, useLanguage} from '@enisco/mcc-react-lib'
import useRegisteredModules from '../../../hooks/useRegisteredModules'
import useFavoriteGroups from '../../../hooks/useFavoriteGroups'
import {App} from '../../../model/apps'
import {Box, List} from '@mui/material'
import {Favorite} from '../../../model/favorites'
import {SearchApp, SearchGroup} from '../../../model/search'
import SearchResultViewer from './SearchResultViewer'
import useRegisteredApps from '../../../hooks/useRegisteredApps'
import {useNavigate} from 'react-router-dom'
import {pathToApp, pathToFavorite} from '../../../util/navigation'

interface Props {
  value: string
  handleClose: () => void
  enterPressed: boolean
  setEnterPressed: (pressed: boolean) => void
}

const SearchResult: React.FC<Props> = ({
  value,
  handleClose,
  enterPressed,
  setEnterPressed
}) => {
  const language = useLanguage()
  const [registeredModules] = useRegisteredModules()
  const [registeredFavoriteGroups] = useFavoriteGroups()
  const [registeredApps] = useRegisteredApps()
  const [highlightedGroup, setHighlightedGroup] = useState(-1)
  const [highlightedApp, setHighlightedApp] = useState(-1)
  const navigate = useNavigate()

  const openApp = useCallback(
    (app: SearchApp) => {
      if (app.app) {
        navigate({pathname: pathToApp(app.app.module, app.app.app)})
      } else if (app.favorite?.id) {
        navigate({pathname: pathToFavorite(app.favorite.id)})
      }
      handleClose()
    },
    [navigate, handleClose]
  )

  const filteredApps = useMemo(() => {
    const compareDescription = (
      name: string | undefined,
      searchString: string
    ) => {
      return name
        ? name.toLocaleLowerCase().includes(searchString.toLocaleLowerCase())
        : false
    }

    const getSearchApps = (apps: App[] | undefined) => {
      const appResult: SearchApp[] = []
      apps?.forEach((app) => {
        appResult.push({
          app: app,
          name: translateText(language, app.description),
          hidden: app.hidden,
          icon: app.icon
        })
      })
      return appResult
    }

    const getSearchFavorites = (favorites: Favorite[] | undefined) => {
      const favoriteResult: SearchApp[] = []
      favorites?.forEach((favorite) => {
        const app = registeredApps.find(
          (regApp) =>
            regApp.app === favorite.app.app &&
            regApp.module === favorite.app.module
        )
        favoriteResult.push({
          favorite: favorite,
          name: translateText(language, favorite.description),
          icon: app ? app.icon : undefined
        })
      })
      return favoriteResult
    }

    const searchResults = (searchString: string) => {
      const resultGroups: SearchGroup[] = []
      registeredModules.forEach((module) => {
        if (
          compareDescription(
            translateText(language, module.description),
            searchString
          )
        ) {
          resultGroups.push({
            name: translateText(language, module.description),
            icon: module.icon,
            apps: getSearchApps(module.apps),
            hidden: module.hidden
          })
        } else {
          const result: App[] | undefined = module.apps?.filter((app) =>
            compareDescription(
              translateText(language, app.description),
              searchString
            )
          )
          if (result && result.length > 0) {
            resultGroups.push({
              name: translateText(language, module.description),
              icon: module.icon,
              apps: getSearchApps(result)
            })
          }
        }
      })
      registeredFavoriteGroups.forEach((group) => {
        if (
          compareDescription(
            translateText(language, group.description),
            searchString
          )
        ) {
          resultGroups.push({
            name: translateText(language, group.description),
            apps: getSearchFavorites(group.favorites)
          })
        } else {
          const result: Favorite[] | undefined = group.favorites?.filter(
            (favorite) =>
              compareDescription(
                translateText(language, favorite.description),
                searchString
              )
          )
          if (result && result.length > 0) {
            resultGroups.push({
              name: translateText(language, group.description),
              apps: getSearchFavorites(result)
            })
          }
        }
      })

      resultGroups.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      for (const group of resultGroups) {
        group.apps.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      }

      return resultGroups
    }

    return searchResults(value).filter((result) => !result.hidden)
  }, [
    language,
    registeredApps,
    registeredModules,
    registeredFavoriteGroups,
    value
  ])

  // Sonar complains about empty arrow functions, but it is a meaningful default here
  const selectNextApp = useRef(() => {
    // NOSONAR
  })
  const selectPreviousApp = useRef(() => {
    // NOSONAR
  })

  useEffect(() => {
    selectNextApp.current = () => {
      if (highlightedApp < filteredApps[highlightedGroup].apps.length - 1) {
        setHighlightedApp(highlightedApp + 1)
      } else if (highlightedGroup < filteredApps.length - 1) {
        setHighlightedGroup(highlightedGroup + 1)
        setHighlightedApp(0)
      }
    }

    selectPreviousApp.current = () => {
      if (highlightedApp > 0) {
        setHighlightedApp(highlightedApp - 1)
      } else if (highlightedGroup > 0) {
        setHighlightedGroup(highlightedGroup - 1)
        setHighlightedApp(filteredApps[highlightedGroup - 1].apps.length - 1)
      }
    }
  }, [
    filteredApps,
    highlightedGroup,
    highlightedApp,
    setHighlightedApp,
    setHighlightedGroup
  ])

  // reset current selection to first app any time the selection changes
  useEffect(() => {
    if (filteredApps.length > 0 && filteredApps[0].apps.length > 0) {
      setHighlightedApp(0)
      setHighlightedGroup(0)
    } else {
      setHighlightedApp(-1)
      setHighlightedGroup(-1)
    }
  }, [language, value, filteredApps])

  useEffect(() => {
    const documentListener = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowUp':
          selectPreviousApp.current()
          break
        case 'ArrowDown':
          selectNextApp.current()
          break
      }
      e.preventDefault()
    }

    document.addEventListener('keyup', documentListener)

    return () => {
      document.removeEventListener('keyup', documentListener)
    }
  }, [])

  useEffect(() => {
    if (enterPressed) {
      if (highlightedGroup >= 0 && highlightedApp >= 0) {
        openApp(filteredApps[highlightedGroup].apps[highlightedApp])
      }
      setEnterPressed(false)
    }
  }, [
    enterPressed,
    setEnterPressed,
    openApp,
    filteredApps,
    highlightedGroup,
    highlightedApp
  ])

  const highlightApp = useCallback(
    (app: SearchApp) => {
      for (let groupId = 0; groupId < filteredApps.length; groupId++) {
        const group = filteredApps[groupId]
        for (let appId = 0; appId < group.apps.length; appId++) {
          const a = group.apps[appId]
          if (
            (app.app &&
              app.app.module === a.app?.module &&
              app.app.app === a.app.app) ||
            (app.favorite && app.favorite.id === a.favorite?.id)
          ) {
            setHighlightedGroup(groupId)
            setHighlightedApp(appId)
          }
        }
      }
    },
    [filteredApps, setHighlightedApp, setHighlightedGroup]
  )

  const getHighlightedApp = (): SearchApp | undefined => {
    if (
      highlightedApp >= 0 &&
      filteredApps.length >= highlightedGroup + 1 &&
      filteredApps[highlightedGroup].apps.length >= highlightedApp + 1
    )
      return filteredApps[highlightedGroup].apps[highlightedApp]
    return undefined
  }

  return (
    <Box data-testid='SearchResult:root'
         sx={{backgroundColor: 'background.paper'}}>
      {
        <List>
          {filteredApps.map((result) => (
            <SearchResultViewer
              handleClose={handleClose}
              result={result}
              searchKey={value}
              key={result.name}
              currentApp={getHighlightedApp()}
              openApp={openApp}
              onMouseOver={highlightApp}
            />
          ))}
        </List>
      }
    </Box>
  )
}

export default SearchResult
